/*
  North Ouest — BOGO card behavior
  - Updates the little color swatch when a selector changes.
  - On "Add to cart", adds BOTH chosen variants as separate line items.
  - After-add behavior (drawer / cart / none) is read from data-after-add.

  The 2-for-1 price is NOT applied here. Set up a Shopify automatic discount
  (Discounts > "Buy X get Y" or a 50%-off-when-qty-2 rule) so the second watch
  is free/half. This script just adds the two items.
*/

if (!customElements.get('bogo-card')) {
  customElements.define(
    'bogo-card',
    class BogoCard extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('.bogo-card__atc');
        this.errorEl = this.querySelector('.bogo-card__error');
        this.selectWraps = Array.from(this.querySelectorAll('.bogo-card__select-wrap'));
        this.afterAdd = this.dataset.afterAdd || 'drawer';
        this.cartUrl = this.dataset.cartUrl || '/cart';
        this.defaultLabel = this.button ? this.button.textContent : 'Add to cart';
      }

      connectedCallback() {
        this.selectWraps.forEach((wrap) => {
          const select = wrap.querySelector('.bogo-card__select');
          const swatch = wrap.querySelector('.bogo-card__swatch');
          if (!select) return;
          this.paintSwatch(select, swatch);
          select.addEventListener('change', () => this.paintSwatch(select, swatch));
        });

        if (this.button) {
          this.button.addEventListener('click', () => this.addToCart());
        }
      }

      paintSwatch(select, swatch) {
        if (!swatch) return;
        const opt = select.options[select.selectedIndex];
        const img = opt && opt.dataset.swatch;
        if (img) {
          swatch.style.backgroundImage = `url(${img})`;
        } else {
          swatch.style.backgroundImage = 'none';
        }
      }

      setError(msg) {
        if (this.errorEl) this.errorEl.textContent = msg || '';
      }

      async addToCart() {
        this.setError('');
        const selects = Array.from(this.querySelectorAll('.bogo-card__select'));
        const items = selects
          .map((s) => parseInt(s.value, 10))
          .filter((id) => !Number.isNaN(id))
          .map((id) => ({ id, quantity: 1 }));

        if (items.length === 0) {
          this.setError('Please choose your colors.');
          return;
        }

        // Block if any chosen variant is sold out.
        const soldOut = selects.some((s) => {
          const opt = s.options[s.selectedIndex];
          return opt && opt.disabled;
        });
        if (soldOut) {
          this.setError('One of the selected colors is sold out.');
          return;
        }

        this.button.classList.add('is-loading');
        this.button.disabled = true;
        this.button.textContent = 'Adding…';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || 'Could not add to cart.');
          }

          this.afterAddBehavior();
        } catch (e) {
          this.setError(e.message || 'Something went wrong. Try again.');
          this.resetButton();
        }
      }

      resetButton() {
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
        this.button.textContent = this.defaultLabel;
      }

      afterAddBehavior() {
        if (this.afterAdd === 'cart') {
          window.location.href = this.cartUrl;
          return;
        }

        if (this.afterAdd === 'none') {
          this.button.textContent = 'Added ✓';
          setTimeout(() => this.resetButton(), 1800);
          // Still notify drawers/widgets that the cart changed.
          this.notifyCartChanged();
          return;
        }

        // Default: open / refresh the cart drawer.
        // Most third-party drawers (incl. Essential Cart Drawer) and Dawn's native
        // drawer listen to one of these. If yours needs a specific call, add it here.
        this.notifyCartChanged();

        // Dawn native drawer support (harmless if absent).
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          fetch(`${window.location.pathname}?sections=cart-drawer`)
            .then((r) => r.json())
            .then((sections) => {
              const html = sections['cart-drawer'];
              if (html && typeof drawer.renderContents === 'function') {
                drawer.renderContents({ sections });
              }
              drawer.open();
            })
            .catch(() => {})
            .finally(() => this.resetButton());
        } else {
          this.resetButton();
        }
      }

      notifyCartChanged() {
        ['cart:refresh', 'cart:updated', 'cart:build', 'ajaxProduct:added'].forEach((name) => {
          document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
          window.dispatchEvent(new CustomEvent(name));
        });
      }
    }
  );
}